
import { Vue, Component, Prop } from "vue-property-decorator";
import IconButton from "@/components/UI/IconButton.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import Dialog from "@/components/UI/Dialog.vue";
import FileUpload from "@/components/Reusable/CustomInputs/FileUpload.vue";
import { pipe } from "fp-ts/lib/function";
import { PropertiesData } from "@/types/tree";
import fpJsonParse from "@/util/fp-json-parse";
import { fold } from "fp-ts/lib/Either";
import slugify from "slugify";
import { triggerDownload } from "@/util/trigger-download";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { configStore } from "@/store/store";

@Component({
  components: {
    IconButton,
    Dropdown,
    Dialog,
    FileUpload
  }
})
export default class ImportExportDropdownMenu extends Vue {
  @Prop({ default: "Global Settings" })
  name!: string;

  @Prop()
  settingsToExport?: Record<string, unknown>;

  @Prop({ default: false })
  canMerge!: boolean;

  @Prop()
  validateSettings!: (settings: PropertiesData) => boolean;

  @Prop()
  importSettings!: (
    settings: PropertiesData,
    merge?: "merge" | "replace"
  ) => void;

  canImport = false;
  importDialogOpen = false;
  settingsToImport = {};
  merge = false;

  get slugifiedName() {
    return slugify(this.name, { lower: true });
  }

  openImportDialog() {
    this.importDialogOpen = true;
  }

  cancelImport() {
    this.settingsToImport = {};
    this.canImport = false;
    this.importDialogOpen = false;
  }

  handleFiles(files: FileList) {
    const file = files?.[0];

    const fileReader = new FileReader();

    fileReader.onloadend = async event => {
      if (!event?.target) return;

      const text = event.target.result as string;

      const parsedJson = pipe(
        fpJsonParse(text),
        fold(
          () => {},
          parsed => parsed as PropertiesData
        )
      );

      if (!this.validateSettings(parsedJson)) {
        this.settingsToImport = {};
        this.canImport = false;
        return;
      }

      this.settingsToImport = parsedJson;
      this.canImport = true;
    };

    fileReader.readAsText(file);
  }

  runImport() {
    this.importSettings(
      this.settingsToImport,
      this.merge ? "merge" : "replace"
    );
    this.settingsToImport = {};
    this.canImport = false;
    this.importDialogOpen = false;
  }

  exportSettings() {
    const settingsJson = jsonStringifyOrUndefined(this.settingsToExport);
    triggerDownload(settingsJson || "", `${this.slugifiedName}.json`);
  }

  get builderMode() {
    return configStore.builderMode;
  }
}
